import { useQuery } from "@tanstack/react-query";
import { loadSupabase } from "@/lib/supabaseLazy";
import { compareTestimonials, resolveTestimonialVideo, type TestimonialVideo } from "@/lib/testimonialVideo";

export type Testimonial = {
  id: string;
  video_url: string;
  poster_url: string | null;
  student_name: string;
  student_title: string | null;
  quote: string | null;
  is_published: boolean;
  sort_order: number;
  created_at: string;
};

/** A row that is known to have a playable video, so the UI never guards again. */
export type PlayableTestimonial = Testimonial & { video: TestimonialVideo };

/** Testimonials change rarely and render on the home page, so they are cached. */
const TESTIMONIALS_STALE_MS = 5 * 60 * 1000;

export const TESTIMONIALS_QUERY_KEY = ["testimonials", "published"] as const;

/**
 * The published testimonials, in display order, already filtered down to rows
 * that will actually play.
 *
 * `is_published` is filtered here as well as in RLS on purpose. The read
 * policy is "published OR admin", which means an admin browsing the public
 * home page would otherwise see their own unpublished drafts and think they
 * were live. The public page shows exactly what a visitor sees; previewing is
 * the admin screen's job.
 */
export const usePublishedTestimonials = () =>
  useQuery({
    queryKey: TESTIMONIALS_QUERY_KEY,
    staleTime: TESTIMONIALS_STALE_MS,
    queryFn: async (): Promise<PlayableTestimonial[]> => {
      const supabase = await loadSupabase();
      const { data, error } = await supabase
        .from("testimonials")
        .select("id, video_url, poster_url, student_name, student_title, quote, is_published, sort_order, created_at")
        .eq("is_published", true)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;

      // A row whose URL no longer resolves is dropped rather than rendered as
      // an empty frame. The DB constraint makes this unlikely, but "unlikely"
      // is not the same as "cannot reach an iframe src".
      return (data as Testimonial[])
        .sort(compareTestimonials)
        .flatMap((row) => {
          const video = resolveTestimonialVideo(row.video_url);
          return video ? [{ ...row, video }] : [];
        });
    },
  });
